import { createFileRoute, redirect, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  ArrowLeft,
  BadgeCheck,
  Edit,
  ExternalLink,
  Loader2,
  Mail,
  Save,
  ThumbsUp,
  Trash2,
  X,
} from 'lucide-react'

import { Main } from '@/components/layout/main'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { superAdminService } from '@/api/super-admin'
import type { LawyerProfileDetail, LawyerProfileUpdate } from '@/types/super-admin'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/stores/authStore'

const ROUTE_ID = '/_authenticated/super-admin-lawyer-profiles/$id'

export const Route = createFileRoute(ROUTE_ID)({
  beforeLoad: async () => {
    const auth = useAuthStore.getState().auth
    if (!auth.accessToken || !auth.user) {
      throw redirect({ to: '/auth/login' })
    }
    if (auth.user.role !== 'Super Admin' && auth.user.role_name !== 'Super Admin') {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: LawyerProfileDetailPage,
})

/** Fields the admin can edit as plain text inputs. */
const TEXT_FIELDS = [
  { key: 'full_name', label: 'Nom complet (AR)' },
  { key: 'full_name_fr', label: 'Nom complet (FR)' },
  { key: 'title', label: 'Titre' },
  { key: 'bar_city', label: 'Ville du barreau' },
  { key: 'phone', label: 'Téléphone' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email_public', label: 'Email public' },
  { key: 'website', label: 'Site web' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'google_maps_url', label: 'Google Maps' },
  { key: 'cabinet_address', label: 'Adresse du cabinet' },
  { key: 'cabinet_city', label: 'Ville du cabinet' },
  { key: 'cabinet_country', label: 'Pays' },
] as const

/** Per-section visibility toggles on the public profile. */
const TOGGLE_FIELDS = [
  { key: 'show_bio', label: 'Afficher la biographie' },
  { key: 'show_contact', label: 'Afficher les contacts' },
  { key: 'show_specializations', label: 'Afficher les spécialités' },
  { key: 'show_experience', label: "Afficher l'expérience" },
  { key: 'show_education', label: 'Afficher la formation' },
  { key: 'show_certifications', label: 'Afficher les certifications' },
  { key: 'show_languages', label: 'Afficher les langues' },
  { key: 'show_career', label: 'Afficher le parcours' },
] as const

const LANDING_BASE = import.meta.env.VITE_LANDING_BASE_URL || 'https://avocatpro.ma'

/** Wording + colour for the moderation status of a public listing request. */
const APPROVAL_STATUS = {
  pending: { label: 'En attente de validation', className: 'bg-amber-50 text-amber-700 border-amber-300' },
  approved: { label: 'Approuvée', className: 'bg-green-50 text-green-700 border-green-300' },
  rejected: { label: 'Rejetée', className: 'bg-red-50 text-red-700 border-red-300' },
} as const

function LawyerProfileDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams({ from: ROUTE_ID })
  const navigate = useNavigate()
  const { toast } = useToast()

  const [profile, setProfile] = useState<LawyerProfileDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isEditMode, setIsEditMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [form, setForm] = useState<LawyerProfileUpdate>({})

  const [confirmDelete, setConfirmDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const [showApprove, setShowApprove] = useState(false)
  const [showReject, setShowReject] = useState(false)
  const [sendReviewLink, setSendReviewLink] = useState(true)
  const [rejectionReason, setRejectionReason] = useState('')
  const [isModerating, setIsModerating] = useState(false)

  const isClaimed = profile?.cabinet_id != null
  // Only rows that came from the public "référencez-moi" form are moderated.
  const isListingRequest = profile?.submission_source === 'public_request'
  const isPending = profile?.approval_status === 'pending'

  const resetForm = (data: LawyerProfileDetail) => {
    setForm({
      full_name: data.full_name,
      full_name_fr: data.full_name_fr,
      title: data.title,
      bar_city: data.bar_city,
      rattachement_year: data.rattachement_year,
      bio_ar: data.bio_ar,
      bio_fr: data.bio_fr,
      phone: data.phone ?? '',
      whatsapp: data.whatsapp,
      email_public: data.email_public ?? '',
      website: data.website,
      linkedin: data.linkedin,
      google_maps_url: data.google_maps_url,
      cabinet_address: data.cabinet_address,
      cabinet_city: data.cabinet_city,
      cabinet_country: data.cabinet_country,
      is_published: data.is_published,
      show_bio: data.show_bio,
      show_contact: data.show_contact,
      show_specializations: data.show_specializations,
      show_experience: data.show_experience,
      show_education: data.show_education,
      show_certifications: data.show_certifications,
      show_languages: data.show_languages,
      show_career: data.show_career,
    })
  }

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await superAdminService.getLawyerProfile(id)
      setProfile(data)
      resetForm(data)
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [id])

  const setField = <K extends keyof LawyerProfileUpdate>(key: K, value: LawyerProfileUpdate[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    if (!form.full_name?.trim()) {
      toast({
        title: t('error') || 'Erreur',
        description: 'Le nom complet est obligatoire.',
        variant: 'destructive',
      })
      return
    }

    setIsSaving(true)
    try {
      const updated = await superAdminService.updateLawyerProfile(id, form)
      setProfile(updated)
      resetForm(updated)
      setIsEditMode(false)
      toast({
        title: t('saved_successfully') || 'Profil mis à jour',
        description: 'Les modifications ont été enregistrées.',
      })
    } catch (e: any) {
      const details = e?.response?.data?.details
      toast({
        title: t('error') || 'Erreur',
        description:
          e?.response?.data?.message ||
          (details ? JSON.stringify(details) : e?.message) ||
          'Échec de la mise à jour',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      // Claimed profiles need the explicit override the API demands.
      await superAdminService.deleteLawyerProfile(id, isClaimed)
      toast({ title: 'Profil supprimé' })
      navigate({ to: '/super-admin-lawyer-profiles' })
    } catch (e: any) {
      toast({
        title: t('error') || 'Erreur',
        description: e?.response?.data?.error || e?.message || 'Échec de la suppression',
        variant: 'destructive',
      })
    } finally {
      setIsDeleting(false)
      setConfirmDelete(false)
    }
  }

  const handleApprove = async () => {
    setIsModerating(true)
    try {
      const updated = await superAdminService.approveListingRequest(id, sendReviewLink)
      setProfile(updated)
      resetForm(updated)
      setShowApprove(false)
      toast({
        title: 'Demande approuvée',
        description:
          sendReviewLink && !updated.review_email_sent
            ? "Profil publié, mais l'email de gestion n'a pas pu être envoyé."
            : 'Le profil est publié dans l\'annuaire.',
      })
    } catch (e: any) {
      toast({
        title: t('error') || 'Erreur',
        description: e?.response?.data?.error || e?.message || "Échec de l'approbation",
        variant: 'destructive',
      })
    } finally {
      setIsModerating(false)
    }
  }

  const handleReject = async () => {
    setIsModerating(true)
    try {
      const updated = await superAdminService.rejectListingRequest(id, rejectionReason.trim())
      setProfile(updated)
      resetForm(updated)
      setShowReject(false)
      setRejectionReason('')
      toast({ title: 'Demande rejetée', description: 'Le profil reste non publié.' })
    } catch (e: any) {
      toast({
        title: t('error') || 'Erreur',
        description: e?.response?.data?.error || e?.message || 'Échec du rejet',
        variant: 'destructive',
      })
    } finally {
      setIsModerating(false)
    }
  }

  const handleSendClaim = async () => {
    try {
      const res = await superAdminService.sendClaimLink(id)
      toast({
        title: res.email_sent ? 'Email envoyé' : 'Lien généré',
        description: res.email_sent
          ? "L'invitation a été envoyée au bureau."
          : "Aucun email public sur ce profil — copiez le lien depuis la liste.",
      })
    } catch (e: any) {
      toast({
        title: t('error') || 'Erreur',
        description: e?.response?.data?.error || e?.message || 'Échec',
        variant: 'destructive',
      })
    }
  }

  if (loading) {
    return (
      <Main>
        <div className="flex h-64 items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>{t('loading') || 'Chargement...'}</span>
        </div>
      </Main>
    )
  }

  if (error || !profile) {
    return (
      <Main>
        <div className="flex h-64 flex-col items-center justify-center gap-4">
          <AlertCircle className="h-10 w-10 text-destructive" />
          <p className="text-center text-base">{error || 'Profil introuvable'}</p>
          <Button variant="outline" onClick={() => navigate({ to: '/super-admin-lawyer-profiles' })}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à la liste
          </Button>
        </div>
      </Main>
    )
  }

  return (
    <Main>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate({ to: '/super-admin-lawyer-profiles' })}
            aria-label="Retour"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{profile.full_name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant={profile.is_published ? 'default' : 'secondary'}>
                {profile.is_published ? 'Publié' : 'Masqué'}
              </Badge>
              {profile.is_verified && (
                <Badge variant="outline" className="gap-1">
                  <BadgeCheck className="h-3 w-3" />
                  Vérifié
                </Badge>
              )}
              <Badge variant={isClaimed ? 'default' : 'outline'}>
                {isClaimed ? `Revendiqué — ${profile.cabinet_name}` : 'Non revendiqué'}
              </Badge>
              {isListingRequest && (
                <Badge variant="outline" className={APPROVAL_STATUS[profile.approval_status].className}>
                  {APPROVAL_STATUS[profile.approval_status].label}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {profile.slug && (
            <Button variant="outline" size="sm" asChild>
              <a
                href={`${LANDING_BASE}/fr/avocats/${profile.slug}/`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Voir en ligne
              </a>
            </Button>
          )}
          {isListingRequest && isPending && (
            <>
              <Button
                size="sm"
                onClick={() => {
                  setSendReviewLink(true)
                  setShowApprove(true)
                }}
              >
                <ThumbsUp className="mr-2 h-4 w-4" />
                Approuver
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setRejectionReason('')
                  setShowReject(true)
                }}
              >
                <X className="mr-2 h-4 w-4" />
                Rejeter
              </Button>
            </>
          )}
          {!isClaimed && (
            <Button variant="outline" size="sm" onClick={handleSendClaim}>
              <Mail className="mr-2 h-4 w-4" />
              Envoyer l'invitation
            </Button>
          )}
          {isEditMode ? (
            <>
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Enregistrer
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={isSaving}
                onClick={() => {
                  resetForm(profile)
                  setIsEditMode(false)
                }}
              >
                <X className="mr-2 h-4 w-4" />
                Annuler
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" onClick={() => setIsEditMode(true)}>
                <Edit className="mr-2 h-4 w-4" />
                Modifier
              </Button>
              <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="mr-2 h-4 w-4" />
                Supprimer
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main details */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Identité et contact</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {TEXT_FIELDS.map(({ key, label }) => (
                <div key={key} className="space-y-2">
                  <Label htmlFor={key}>{label}</Label>
                  <Input
                    id={key}
                    value={(form[key] as string) ?? ''}
                    disabled={!isEditMode}
                    onChange={(e) => setField(key, e.target.value)}
                  />
                </div>
              ))}
              <div className="space-y-2">
                <Label htmlFor="rattachement_year">Année d'inscription au barreau</Label>
                <Input
                  id="rattachement_year"
                  type="number"
                  value={form.rattachement_year ?? ''}
                  disabled={!isEditMode}
                  onChange={(e) =>
                    setField('rattachement_year', e.target.value ? Number(e.target.value) : null)
                  }
                />
                {profile.years_of_experience != null && (
                  <p className="text-xs text-muted-foreground">
                    {profile.years_of_experience} ans d'expérience (calculé)
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Biographie</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bio_ar">Biographie (AR)</Label>
                <Textarea
                  id="bio_ar"
                  rows={6}
                  dir="rtl"
                  value={form.bio_ar ?? ''}
                  disabled={!isEditMode}
                  onChange={(e) => setField('bio_ar', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio_fr">Biographie (FR)</Label>
                <Textarea
                  id="bio_fr"
                  rows={6}
                  value={form.bio_fr ?? ''}
                  disabled={!isEditMode}
                  onChange={(e) => setField('bio_fr', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Structured blocks are read-only here: they are JSON lists whose shape
              the API validates, and a free-text editor would let an admin break
              the public page. */}
          <Card>
            <CardHeader>
              <CardTitle>Données structurées</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <StructuredBlock
                label="Spécialités"
                items={profile.specializations.map((s) => s.fr || s.ar || s.key)}
              />
              <Separator />
              <StructuredBlock
                label="Formation"
                items={profile.education.map((e) => `${e.degree} — ${e.institution} (${e.year})`)}
              />
              <Separator />
              <StructuredBlock
                label="Certifications"
                items={profile.certifications.map((c) => `${c.title} — ${c.issuer} (${c.year})`)}
              />
              <Separator />
              <StructuredBlock
                label="Parcours"
                items={profile.career.map((c) => `${c.role} — ${c.cabinet}, ${c.city}`)}
              />
              <Separator />
              <StructuredBlock label="Langues" items={profile.languages_spoken} />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {isListingRequest && (
            <Card className={isPending ? 'border-amber-300' : undefined}>
              <CardHeader>
                <CardTitle>Demande de référencement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Statut</p>
                  <Badge variant="outline" className={APPROVAL_STATUS[profile.approval_status].className}>
                    {APPROVAL_STATUS[profile.approval_status].label}
                  </Badge>
                </div>
                <ReadOnlyRow
                  label="Reçue le"
                  value={new Date(profile.created_at * 1000).toLocaleString()}
                />
                <ReadOnlyRow
                  label="Traitée le"
                  value={profile.reviewed_at ? new Date(profile.reviewed_at * 1000).toLocaleString() : ''}
                />
                {profile.rejection_reason && (
                  <ReadOnlyRow label="Motif du rejet" value={profile.rejection_reason} />
                )}
                {isPending && (
                  <p className="pt-1 text-xs text-muted-foreground">
                    Ce profil reste invisible dans l'annuaire public tant que la demande n'est pas approuvée.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Visibilité</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <Label htmlFor="is_published" className="font-medium">
                  Publié dans l'annuaire
                </Label>
                <Switch
                  id="is_published"
                  checked={!!form.is_published}
                  disabled={!isEditMode}
                  onCheckedChange={(v) => setField('is_published', v)}
                />
              </div>
              <Separator />
              {TOGGLE_FIELDS.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <Label htmlFor={key} className="text-sm font-normal">
                    {label}
                  </Label>
                  <Switch
                    id={key}
                    checked={!!form[key]}
                    disabled={!isEditMode}
                    onCheckedChange={(v) => setField(key, v)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>SEO</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <ReadOnlyRow label="Slug" value={profile.slug} />
              <ReadOnlyRow label="URL canonique" value={profile.canonical_url} />
              <ReadOnlyRow label="Titre SEO (FR)" value={profile.seo_title_fr} />
              <ReadOnlyRow label="Titre SEO (AR)" value={profile.seo_title_ar} />
              <p className="pt-2 text-xs text-muted-foreground">
                Ces champs sont régénérés automatiquement à chaque enregistrement.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Approve listing request */}
      <Dialog open={showApprove} onOpenChange={setShowApprove}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approuver la demande</DialogTitle>
            <DialogDescription>
              Le profil de <strong>{profile.full_name}</strong> sera publié dans l'annuaire public.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 py-2">
            <Checkbox
              id="send-review-link"
              checked={sendReviewLink}
              onCheckedChange={(checked) => setSendReviewLink(checked === true)}
            />
            <div className="grid gap-1">
              <Label htmlFor="send-review-link" className="cursor-pointer">
                Envoyer le lien de gestion à l'avocat
              </Label>
              <p className="text-xs text-muted-foreground">
                Permet à l'avocat de confirmer ou corriger ses informations après publication.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprove(false)}>
              Annuler
            </Button>
            <Button onClick={handleApprove} disabled={isModerating}>
              {isModerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Approuver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject listing request */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeter la demande</DialogTitle>
            <DialogDescription>
              Le profil de <strong>{profile.full_name}</strong> restera non publié.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="rejection-reason">Motif du rejet (interne)</Label>
            <Textarea
              id="rejection-reason"
              rows={3}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Ex. inscription au barreau non vérifiable"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={isModerating}>
              {isModerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Rejeter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Supprimer ce profil ?"
        desc={
          isClaimed
            ? `Ce profil est revendiqué par ${profile.cabinet_name}. La suppression détruira sa présence publique. Cette action est irréversible.`
            : 'Cette action est irréversible. Pour retirer le profil de l\'annuaire sans le détruire, désactivez plutôt « Publié dans l\'annuaire ».'
        }
        confirmText="Supprimer"
        destructive
        isLoading={isDeleting}
        handleConfirm={handleDelete}
      />
    </Main>
  )
}

function StructuredBlock({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <p className="mb-2 font-medium">{label}</p>
      {items.length === 0 ? (
        <p className="text-muted-foreground">—</p>
      ) : (
        <ul className="list-inside list-disc space-y-1 text-muted-foreground">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="break-all">{value || '—'}</p>
    </div>
  )
}
